/* eslint-disable @nx/enforce-module-boundaries */
import {
  Brifecase,
  Currency,
  Industry,
  JobLocationIcon,
  CompanyIndustryIcon,
  SaveJobs,
} from '@mybridge/icons';
import {
  Box,
  Button,
  HStack,
  Heading,
  Image,
  Link,
  SeeMore,
  Stack,
  Text,
  VStack,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from '@mybridge/ui';
import { forwardRef, useContext, useState } from 'react';
import { SavedJobIcon } from '@mybridge/icons/SavedJob';
import {
  convertHTMLToPlainText,
  currencyFormatter,
  formatLocation,
} from 'v4/lib/commons';
import { EMPLOYMENT_CHOICES, WORKPLACE_TYPE } from 'v4/lib/data';
import { isMobile } from 'react-device-detect';
import { JobsListingContext } from 'v4/snippets/jobs/context';
import axios from 'axios';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { updateCandidateStatus } from 'v4/store/actions/companyJobs.actions';
import { useNumberFormatter } from '@mybridge/ui/custom-hooks/useNumberFormatter';
export const AppliedJobCard = forwardRef(
  (
    {
      job,
      onViewDetailsClick,
      href,
      onSaveClick,
      saveLoading = false,
      isSaved = false,
      ...props
    },
    ref
  ) => {

    const {
      refetchAppliedJobsListing,
    } = useContext(JobsListingContext);
    
    const { job_details: jd } = job ?? {};
    const {
      hiring_company_details: company,
      currency_choice: currency,
      compensation_range_min_amt: minSalary,
      compensation_range_max_amt: maxSalary,
      benefits,
      employment_type: empType,
      job_description: description,
      payment_choice,
      remote,
    } = jd ?? {};
    const { t } = useTranslator();
    const {companyPageList} = useSelector((state) => state.companyPage);
    const isPrivatePage = companyPageList?.some((page) => page?.id === job?.job_details?.hiring_company_details?.id);
    const { push } = useRouter(); 
    const dispatch = useDispatch();
    const {formatNumber} =useNumberFormatter() 
    const descPlainText = convertHTMLToPlainText(description ?? '');
    const formatDate = (dateString) => {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    };

    const [withdrawLoading, setWithdrawLoading] = useState(false);
    const [showConfirmWithdraw, setShowConfirmWithdraw] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);

    const handleWithdraw = async (job) => {
      if (!job) {
        console.error("No job details found");
        return;
      }
    
      const candidateId = job?.candidate_details?.id;
    
      if (!job?.id || !candidateId) {
        console.error("Missing job ID or candidate ID");
        return;
      }
    
      setWithdrawLoading(true);
      try {
        // const response = await axios.put(
        //   `https://api.bybk.org/api/v1/jobs/${job.jobs_id}/candidate/${candidateId}/status/update`,
        //   { status: 'REJECTED' }
        // );
        const response = await dispatch(updateCandidateStatus({
          jobId: job.jobs_id,
          candidateId: candidateId,
          status: 'REJECTED'
        }));

        refetchAppliedJobsListing?.();
      } catch (error) {
        console.error('Error withdrawing application:', error);
      } finally {
        setWithdrawLoading(false);
        setShowConfirmWithdraw(false);
      }
    };
    
    const handleConfirmWithdraw = (job) => {
      setSelectedJob(job);
      setShowConfirmWithdraw(true);
    };
    
    const handleCloseConfirmWithdraw = () => {
      setShowConfirmWithdraw(false);
      setSelectedJob(null);
    };
    
    const prevDefaultCompanyImg = "https://cdn.bybk.org/media/placeholder/logo_placeholder_image.png"
    const defaultCompanyImg = "https://cdn.bybk.org/media/placeholder/logo-placeholder-image.png"
  
    return (
      <>
        <Stack {...props} h="100%" >
          {/* Header Section */}
          <HStack alignItems="flex-start" mb={3}>
            <HStack flex={1}>
              <Image
                border="1px solid"
                borderColor="blackAlpha.100"
                src={company?.image?.includes("logo_placeholder_image.png") ? defaultCompanyImg : company?.image ?? defaultCompanyImg}
                w="12"
                h="12"
                alt={company?.name}
                borderRadius="50%"
              />
              <VStack align="start" spacing={0} ml={2}>
                <Heading
                  {...(href ? { as: Link, href } : {})}
                  color="#2E2E2A"
                  size="sm"
                  fontWeight="500"
                >
                  {jd?.title}
                </Heading>
                <Text size="14px" color="#5B5B5B" fontWeight="500">
                  {jd?.days_posted === 0 ? "few minutes ago" : `${formatNumber(jd?.days_posted)} ${t('jobs.daysAgo') || "days ago"}`}
                </Text>
              </VStack>
            </HStack>
          </HStack>

          {/* Content Section */}
          <Stack flex={1} spacing={2} h={'180px'}>
            <HStack
              alignItems="flex-start"
              cursor="pointer"
              spacing={2}
              onClick={() => {
                isPrivatePage
                  ? push(`/page/private/${job?.job_details?.hiring_company_details?.id}`)
                  : push(`/c/${job?.job_details?.hiring_company_details?.slug}`);
              }}
            >
              <CompanyIndustryIcon color="#9F9F9F" />
              <Text display="inline" fontSize="sm" isTruncated>
                {company?.name}
              </Text>
            </HStack>

            {formatLocation(jd) && (
              <HStack alignItems="flex-start" spacing={2}>
                <JobLocationIcon color="#9F9F9F" />
                <Text display="inline" fontSize="sm" isTruncated>
                  {formatLocation(jd)}
                </Text>
              </HStack>
            )}

            {minSalary > 0 && (
              <HStack alignItems="flex-start" spacing={2}>
                <Box pt="2px">
                  <Currency color="#9F9F9F" />
                </Box>
                {maxSalary !== null && maxSalary > 0 ? (
                  <Text fontSize="sm" isTruncated>
                    {currencyFormatter(minSalary, currency)} to{' '}
                    {currencyFormatter(maxSalary, currency)} {payment_choice}
                  </Text>
                ) : (
                  <Text fontSize="sm" isTruncated>
                    {currencyFormatter(minSalary, currency)} {payment_choice}
                  </Text>
                )}
              </HStack>
            )}

            {benefits?.length > 0 && (
              <HStack alignItems="flex-start" spacing={2}>
                <Box pt="2px">
                  <Industry color="#9F9F9F" />
                </Box>
                <Text fontSize="sm" isTruncated={!isMobile}>
                  {benefits?.map?.((b) => b?.name)?.join(', ')}
                </Text>
              </HStack>
            )}

            <HStack alignItems="flex-start" spacing={2}>
              <Box pt="2px">
                <Brifecase color="#9F9F9F" />
              </Box>
              <Text fontSize="sm" isTruncated>
                {EMPLOYMENT_CHOICES?.find((ec) => ec.value === empType)?.label}{' '}
                | {WORKPLACE_TYPE?.find((ec) => ec.value === remote)?.label}
              </Text>
            </HStack>
          </Stack>

          {/* Footer Section */}
          <HStack justifyContent="flex-start" >
            <Button
              border="1px solid #C7C7C7"
              borderRadius="50px"
              bg="none"
              fontSize="14px"
              h="41px"
              color="green.500"
              fontWeight="500"
              cursor={job.status === 'REJECTED' ? 'auto' : 'pointer'}
            >
              {t('jobs.applied') || "Applied"} | {formatDate(jd?.applied_date)}
            </Button>
            <Button
              border="1px solid #C7C7C7"
              borderRadius="50px"
              bg="none"
              fontSize="14px"
              h="41px"
              color="#FF0600"
              fontWeight="500"
              onClick={() => job.status !== 'REJECTED' && handleConfirmWithdraw(job)}
              isLoading={withdrawLoading}
              cursor={job.status === 'REJECTED' ? 'not-allowed' : 'pointer'}
              isDisabled={job?.status === 'REJECTED'}
            >
              {job.status === 'REJECTED' ? t('jobs.withdrawn') || 'Withdrawn' : t('jobs.withdraw') || 'Withdraw'}
            </Button>
            
            <Modal isOpen={showConfirmWithdraw} onClose={handleCloseConfirmWithdraw} isCentered>
              <ModalOverlay />
              <ModalContent w={350} h="auto" p={2} pr={4}>
                <ModalHeader fontWeight="bold" fontSize="lg" p={0}>
                  <ModalCloseButton />
                </ModalHeader>
                <ModalBody>
                  <Text fontSize="16px" fontWeight="500" color="#5B5B5B" mb={2}>
                    {'Withdraw Application?'}
                  </Text>
                  <Text fontSize="14px" color="#5B5B5B">
                    {'Are you sure you want to withdraw your application for this job?'}
                  </Text>
                </ModalBody>
                <ModalFooter pr={0} pt={4}>
                  <Button
                    fontSize={16}
                    color={'#4F4F4F'}
                    variant="ghost"
                    borderRadius="full"
                    mr={3}
                    onClick={handleCloseConfirmWithdraw}
                  >
                    {t('buttons.cancel') || 'Cancel'}
                  </Button>
                  <Button
                    fontSize={15}
                    color={'#FFFFFF'}
                    backgroundColor={'#FF0600'}
                    _hover={{backgroundColor:''}}
                    borderRadius="full"
                    onClick={() => handleWithdraw(selectedJob)}
                    isLoading={withdrawLoading}
                  >
                    {t('jobs.withdraw') || 'Withdraw'}
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
            <IconButton
              isLoading={saveLoading}
              onClick={onSaveClick}
              border="1px solid gray"
              borderRadius="50px"
              bg="none"
              color="#5B5B5B"
              fontSize="14px"
              icon={
                isSaved ? (
                  <SavedJobIcon width="20" height="20" />
                ) : (
                  <SaveJobs width="20" height="20" />
                )
              }
            />
          </HStack>
        </Stack>
      </>
    );
  }
);
